import React, { ChangeEvent, useEffect, useState } from "react";
import {
  getSpecies,
  createSpecies,
  deleteSpecies,
  updateSpecies,
  getSpeciesById,
} from "../../api/species/speciesApi";
import { getIUCNStatus } from "../../api/iucnstatus/iucnstatusApi";
import {
  Species,
  IUCNStatus,
  PopulationTrend,
  SpeciesCategory,
} from "../common/ObjectTypes";
import { toast } from "react-toastify";
import { getPopulationTrends } from "../../api/populationtrend/populationtrendApi";
import { getSpeciesCategories } from "../../api/speciesCategory/speciesCategoryApi";
import { MoonLoader } from "react-spinners";
import AddEditSpecies from "./AddEditSpecies";
import { useNavigate, useParams } from "react-router-dom";

type ManageAddEditSpeciesProps = {
  existingSpecies: boolean;
};

const ManageAddEditSpecies = ({
  existingSpecies,
}: ManageAddEditSpeciesProps) => {
  const { v4: uuidv4 } = require("uuid");
  const [pageLoaded, setPageLoaded] = useState(false);
  const navigate = useNavigate();
  const [addSpecies, setAddSpecies] = useState(false);
  const { id: speciesId } = useParams<{ id: string }>(); // Extract the `id` parameter
  const [errors, setErrors] = useState<any>({});
  const [species, setSpecies] = useState<Species>({
    name: "",
    speciesId: "",
    colloquialName: "",
    scientificName: "",
    description: "",
    populationTrendId: "",
    iucnStatusId: "",
    speciesCategoryId: "",
    rowVersion: "",
  });
  const [iucnStatuses, setIucnStatuses] = useState<IUCNStatus[]>([]);
  const [populationTrends, setPopulationTrends] = useState<PopulationTrend[]>(
    []
  );
  const [speciesCategories, setSpeciesCategories] = useState<SpeciesCategory[]>(
    []
  );

  useEffect(() => {
    const loadAllData = async () => {
      try {
        // Fetch all necessary data in parallel
        const [
          fetchedStatuses,
          fetchedTrends,
          fetchedCategories,
          fetchedSpecies,
        ] = await Promise.all([
          getIUCNStatus(),
          getPopulationTrends(),
          getSpeciesCategories(),
          existingSpecies && speciesId
            ? getSpeciesById(speciesId)
            : Promise.resolve(null),
        ]);

        // Set the fetched data to state
        setIucnStatuses(fetchedStatuses);
        setPopulationTrends(fetchedTrends);
        setSpeciesCategories(fetchedCategories);

        if (fetchedSpecies) {
          setSpecies(fetchedSpecies);
        }

        // Mark the page as loaded only after all data is fetched
        setPageLoaded(true);
      } catch (err) {
        console.error(err); // Log the error for debugging
      }
    };

    if (!pageLoaded) {
      loadAllData();
    }
  }, [pageLoaded, existingSpecies, speciesId]);

  const handleSelectSpecies = (species: Species) => {
    setSpecies({
      name: species.name,
      speciesId: species.speciesId,
      colloquialName: species.colloquialName,
      scientificName: species.scientificName,
      description: species.description,
      populationTrendId: species.populationTrendId,
      iucnStatusId: species.iucnStatusId,
      speciesCategoryId: species.speciesCategoryId,
      rowVersion: species.rowVersion,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setSpecies((prev: Species) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeSingleSelect = (name: string, e: any) => {
    const newValue = e === null ? null : e.value;
    setSpecies((prevSpecies: any) => ({
      ...prevSpecies,
      [name]: newValue,
    }));
  };

  const formIsValid = (): boolean => {
    const newErrors: any = {};
    if (!species.name) {
      newErrors.name = "Species name is required";
    }

    if (!species.iucnStatusId) {
      newErrors.iucnStatus = "IUCN status is required";
    }
    if (!species.populationTrendId) {
      newErrors.trend = "Population trend is required";
    }
    if (!species.speciesCategoryId) {
      newErrors.speciesCategory = "Species category is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e: any) => {
    e.preventDefault();
    const updatedSpecies = {
      name: species.name,
      colloquialName: species.colloquialName,
      scientificName: species.scientificName,
      description: species.description,
      populationTrendId: species.populationTrendId,
      iucnStatusId: species.iucnStatusId,
      speciesCategoryId: species.speciesCategoryId,
      speciesId: species.speciesId || uuidv4(),
    };
    try {
      if (!formIsValid()) return;
      if (!existingSpecies) {
        await createSpecies(updatedSpecies);
      } else {
        await updateSpecies(updatedSpecies);
      }
      toast.success(species.name + (existingSpecies ? " Updated" : " Added."), {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      navigate("/species");
      setAddSpecies(false);
      setPageLoaded(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancelEdit = () => {
    setSpecies({
      name: "",
      speciesId: "",
      colloquialName: "",
      scientificName: "",
      description: "",
      populationTrendId: "",
      iucnStatusId: "",
      speciesCategoryId: "",
    });
    navigate("/species");
  };

  const handleDelete = async () => {
    try {
      await deleteSpecies(species.speciesId ? species.speciesId : "");
      setSpecies({
        name: "",
        speciesId: "",
        colloquialName: "",
        scientificName: "",
        description: "",
        populationTrendId: "",
        iucnStatusId: "",
        speciesCategoryId: "",
        rowVersion: "",
      });
      toast.success("Species was deleted.", {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      setPageLoaded(false);
    } catch (error) {
      console.log(error);
    }
  };
  return !pageLoaded ? (
    <div className="flex justify-center items-center">
      <MoonLoader color="#9EB45B" />
    </div>
  ) : (
    <div className="">
      <AddEditSpecies
        species={species}
        iucnStatuses={iucnStatuses}
        populationTrends={populationTrends}
        speciesCategories={speciesCategories}
        errors={errors}
        addSpecies={addSpecies}
        onSelectSpecies={handleSelectSpecies}
        onCancelEdit={handleCancelEdit}
        onChange={handleChange}
        onSave={handleSave}
        onChangeSingleSelect={handleChangeSingleSelect}
        onDelete={handleDelete}
        existingSpecies={existingSpecies}
      />
    </div>
  );
};

export default ManageAddEditSpecies;
